import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { disconnectSocket, getSocket } from '@/api/socket'
import { subscribeActiveCompanyChannel } from '@/domains/companies'

/** cross-tab + background-tab sync for the active-company cookie.
 *
 *  cross-tab: peer tabs post on BroadcastChannel when they switch /
 *  create. this hook invalidates every query + reconnects the socket so
 *  the peer tab's React tree picks up the new tenant scope. unlike the
 *  local soft transition we do NOT navigate: the peer chose its own
 *  route and we don't get to override it. they'll see the loaders
 *  refetch and (if the new scope makes the current URL invalid) a 404
 *  or empty state, same as the v1 URL-fallthrough contract.
 *
 *  background-tab: when this tab regains visibility, refetch the
 *  canonical pair ([me, companies] + [setup]) to detect drift from
 *  something that happened while we were hidden (peer switch we missed
 *  a BroadcastChannel for, server-side cookie change, etc.). cheap, just
 *  two GETs.
 *
 *  mount once in a long-lived container that survives route changes.
 *  SidebarCompanyHeader is the right home since it ships in both
 *  AppShell and OnboardingShell.
 */
export function useActiveCompanySync(): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    return subscribeActiveCompanyChannel(() => {
      disconnectSocket()
      getSocket()
      void queryClient.invalidateQueries()
    })
  }, [queryClient])

  useEffect(() => {
    if (typeof document === 'undefined') return
    function onVisibilityChange(): void {
      if (document.visibilityState !== 'visible') return
      void queryClient.invalidateQueries({ queryKey: ['me', 'companies'] })
      void queryClient.invalidateQueries({ queryKey: ['setup'] })
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [queryClient])
}
